import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useContactPreview } from '../../context/ContactPreviewContext';
import { api } from '../../services/api';
import { getAvatarStyle } from '../../utils/avatar';
import { contactAge } from '../../utils/dateUtils';
import './ContactPreviewPanel.css';

// ── Helpers ───────────────────────────────────────────────────────────────────
const GENDER_LABEL = { monsieur: 'M.', madame: 'Mme', autre: 'Autre' };
const STATUS_LABEL  = { active: 'Actif', inactive: 'Inactif', prospect: 'Prospect' };
const STATUS_COLOR  = { active: '#16B886', inactive: '#9CA3AF', prospect: '#F59E0B' };
const TONE_LABEL    = { tutoiement: 'Tu', vouvoiement: 'Vous' };

// Âge : brique unique partagée (gère la sentinelle « année inconnue » et
// répare les années à 2 chiffres). Voir src/utils/dateUtils.js.
const getAge = contactAge;

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(String(iso).replace(' ', 'T')).toLocaleDateString('fr-CH', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return null; }
}

function ScoreBar({ label, value, color }) {
  return (
    <div className="cpanel-score-row">
      <span className="cpanel-score-label">{label}</span>
      <div className="cpanel-score-track">
        <div className="cpanel-score-fill" style={{ width: `${value ?? 0}%`, background: color }} />
      </div>
      <span className="cpanel-score-pct" style={{ color }}>{value ?? 0}</span>
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────
export default function ContactPreviewPanel() {
  const { previewId, closePreview } = useContactPreview();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchContact = useCallback(async (id) => {
    setLoading(true);
    setContact(null);
    try {
      const data = await api.getContact(id);
      setContact(data);
    } catch (e) {
      console.error('ContactPreview: failed to fetch', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (previewId) fetchContact(previewId);
    else setContact(null);
  }, [previewId, fetchContact]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closePreview(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closePreview]);

  const isOpen = !!previewId;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`cpanel-backdrop ${isOpen ? 'cpanel-backdrop-visible' : ''}`}
        onClick={closePreview}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside className={`cpanel ${isOpen ? 'cpanel-open' : ''}`} aria-label="Fiche contact">
        {loading && (
          <div className="cpanel-loading">
            <div className="cpanel-spinner" />
          </div>
        )}

        {!loading && contact && (
          <>
            {/* ── Header ── */}
            <div className="cpanel-header">
              <div className="cpanel-avatar" style={getAvatarStyle(contact.first_name)}>
                {(contact.first_name || '?').charAt(0)}{(contact.last_name || '').charAt(0)}
              </div>
              <div className="cpanel-header-info">
                <div className="cpanel-name">
                  {contact.gender && GENDER_LABEL[contact.gender] && (
                    <span className="cpanel-gender">{GENDER_LABEL[contact.gender]}</span>
                  )}
                  {contact.first_name} {contact.last_name}
                </div>
                <div className="cpanel-pills">
                  {contact.status && (
                    <span className="cpanel-pill" style={{ background: `${STATUS_COLOR[contact.status]}18`, color: STATUS_COLOR[contact.status] }}>
                      {STATUS_LABEL[contact.status] || contact.status}
                    </span>
                  )}
                  {contact.category && (
                    <span className="cpanel-pill cpanel-pill-violet">{contact.category}</span>
                  )}
                  {contact.tone && TONE_LABEL[contact.tone] && (
                    <span className="cpanel-pill cpanel-pill-gray">{TONE_LABEL[contact.tone]}</span>
                  )}
                </div>
              </div>
              <div className="cpanel-header-actions">
                <Link to={`/contacts/${contact.id}`} className="cpanel-btn-full" onClick={closePreview} title="Voir la fiche complète">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M5 12h14M15 6l6 6-6 6"/></svg>
                </Link>
                <button className="cpanel-btn-close" onClick={closePreview} aria-label="Fermer">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            {/* ── Contact info ── */}
            <div className="cpanel-section">
              {contact.email && (
                <div className="cpanel-row">
                  <span className="cpanel-row-icon">✉️</span>
                  <a href={`mailto:${contact.email}`} className="cpanel-row-val cpanel-link">{contact.email}</a>
                </div>
              )}
              {contact.phone && (
                <div className="cpanel-row">
                  <span className="cpanel-row-icon">📱</span>
                  <a href={`tel:${contact.phone}`} className="cpanel-row-val cpanel-link">{contact.phone}</a>
                </div>
              )}
              {(contact.city || contact.region) && (
                <div className="cpanel-row">
                  <span className="cpanel-row-icon">📍</span>
                  <span className="cpanel-row-val">{[contact.city, contact.region].filter(Boolean).join(', ')}</span>
                </div>
              )}
              {contact.birth_date && (
                <div className="cpanel-row">
                  <span className="cpanel-row-icon">🎂</span>
                  <span className="cpanel-row-val">
                    {new Date(`1970-${contact.birth_date.slice(5, 10)}`).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long' })}
                    {getAge(contact.birth_date) !== null && (
                      <span className="cpanel-muted"> · {getAge(contact.birth_date)} ans</span>
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* ── Scores ── */}
            {(contact.engagement_score != null || contact.quality_score != null) && (
              <div className="cpanel-section">
                <div className="cpanel-section-title">Profil relationnel</div>
                {contact.engagement_score != null && (
                  <ScoreBar label="Engagement" value={contact.engagement_score}
                    color={contact.engagement_score >= 70 ? '#16B886' : contact.engagement_score >= 40 ? '#F59E0B' : '#EF4444'} />
                )}
                {contact.quality_score != null && (
                  <ScoreBar label="Qualité fiche" value={contact.quality_score}
                    color={contact.quality_score >= 70 ? '#6E45F5' : contact.quality_score >= 50 ? '#F59E0B' : '#EF4444'} />
                )}
              </div>
            )}

            {/* ── Mémoire relationnelle ── */}
            {contact.relationship_memory && (
              <div className="cpanel-section">
                <div className="cpanel-section-title">Note Appee</div>
                <div className="cpanel-memory">
                  <span className="cpanel-memory-icon">💡</span>
                  <p className="cpanel-memory-text">{contact.relationship_memory}</p>
                </div>
              </div>
            )}

            {/* ── Dernier email ── */}
            {contact.last_email_sent && (
              <div className="cpanel-section">
                <div className="cpanel-section-title">Dernier contact</div>
                <div className="cpanel-row">
                  <span className="cpanel-row-icon">📬</span>
                  <span className="cpanel-row-val">
                    Envoyé le {formatDate(contact.last_email_sent)}
                    {contact.last_email_opened && (
                      <span className="cpanel-badge-read"> · Lu ✓</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* ── RGPD ── */}
            <div className="cpanel-section cpanel-section-rgpd">
              <span className={`cpanel-rgpd ${contact.gdpr_consent ? 'cpanel-rgpd-ok' : 'cpanel-rgpd-no'}`}>
                {contact.gdpr_consent ? '🔓 Consentement RGPD' : '🔒 Pas de consentement RGPD'}
              </span>
            </div>

            {/* ── Quick actions ── */}
            <div className="cpanel-actions">
              {contact.phone && (
                <a href={`tel:${contact.phone}`} className="cpanel-action-btn">
                  <span>📞</span> Appeler
                </a>
              )}
              {contact.phone && (
                <a href={`sms:${contact.phone}?body=${encodeURIComponent(`Bonjour ${contact.first_name}, `)}`} className="cpanel-action-btn">
                  <span>💬</span> SMS
                </a>
              )}
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="cpanel-action-btn">
                  <span>✉️</span> Email
                </a>
              )}
              <Link to={`/contacts/${contact.id}`} onClick={closePreview} className="cpanel-action-btn cpanel-action-primary">
                <span>👤</span> Fiche complète
              </Link>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
